import { useState, useCallback, useEffect } from "react";
import SectionContainer from "../../components/UI/SectionContainer";
import UserSearchBox from "../../components/UI/UserSearchBox";
import Pagination from "../../components/UI/Pagination";
import DateTimeCell from '../../components/UI/DateTimeCell';
import { openAlert, exportExcel, showPopup, USER_STATE } from '../../utils/common';
import { getGlucoState } from '../../utils/formatter';
import Spinner from '../../components/UI/Spinner';
import glucoService from '../../services/glucoService';

const GlucoDataPage = () => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchKind, setSearchKind] = useState('0');
    const [keyword, setKeyword] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const pageSize = 10;
    const pageBlock = 5;

    const fetchList = useCallback(async (page = 1, skArg, kwArg) => {
      setLoading(true);
      try {
        const sk = skArg !== undefined ? Number(skArg) : 0;
        const kwState = kwArg !== undefined ? kwArg : '';
        
        const result = await glucoService.getGlucoList({
          page,
          pageSize,
          searchKind: sk,
          keyword: kwState
        });
        
        if (result.success) {
          setList(result.data.glucos);
          setTotalCount(result.data.pagination?.totalCount);
        } else {
          openAlert({ title: '알림', description: result.message });
        }
      } catch (e) { 
        const errorMessage = e.response?.data?.message || e.message || '데이터 로드 중 오류가 발생했습니다.';
        openAlert({ title: '알림', description: errorMessage });
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => {
      fetchList(currentPage, searchKind, keyword);
    }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = (directKeyword) => { 
      setCurrentPage(1); 
      // Use directKeyword if provided (from suggestion select), otherwise use keyword from state
      fetchList(1, searchKind, directKeyword !== undefined ? directKeyword : keyword); 
    };
    const handleReset = () => { 
      setSearchKind('0'); 
      setKeyword(''); 
      setCurrentPage(1); 
      // Call fetchList directly with reset values, consistent with new handleSearch signature
      fetchList(1, '0', ''); 
    };

    const showGlucoData = (idx) => {
      showPopup(`/user/gluco/${idx}`, 'userinfo');
    }

    const handleExcelDownload = async () => {
        setLoading(true);
        try {
            const result = await glucoService.downloadGlucoExcel({
                searchKind: Number(searchKind),
                keyword: keyword
            });
            
            if (result.success) {
                const excelConfig = {
                    excelFileName : "혈당 측정 데이터 수신.xlsx",
                    sheetName : "Sheet1"
                }
                exportExcel(result.data, excelConfig);
            } else {
                openAlert({ title: '알림', description: result.message });
            }
        } catch (e) {
            const errorMessage = e.response?.data?.message || e.message || '엑셀 다운로드 중 오류가 발생했습니다.';
            openAlert({ title: '알림', description: errorMessage });
        } finally {
            setLoading(false);
        }
    }

    return (
        <SectionContainer title="혈당 수치 조회">
            <UserSearchBox
              value={keyword}
              userState={USER_STATE.NORMAL}
              onChange={setKeyword}
              searchKind={searchKind}
              onSelect={setSearchKind}
              onSearch={handleSearch}
              onReset={handleReset}
              onSuggestSelect={selectedVal => { 
                setKeyword(selectedVal); // Update the keyword state
                handleSearch(selectedVal); // And immediately search with this selected value
              }}
              totalCount={totalCount}
              showTotalCount={false}
              onExcelDownload={handleExcelDownload}
              customFetchSuggestions={glucoService.getSearchSuggest}
            />
            <div className="content_area">
              <Spinner visible={loading} />
              <div className="table_area">
                <div className="table">
                  <table className="gluco-data-table">
                    <thead>
                      <tr>
                        <th>성명</th>
                        <th>이메일</th>
                        <th>최근 측정결과</th>
                        <th>측정상태</th>
                        <th>최근 접속일</th>
                        <th>최근 측정일</th>
                        <th>관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!loading && list.length === 0 ? (
                        <tr><td colSpan="7">혈당 측정 데이터가 존재하지 않습니다.</td></tr>
                      ) : (
                        list.map((item, idx) => (
                          <tr key={idx}>
                            <td style={{cursor: 'default'}}>{item.userName}</td>
                            <td style={{cursor: 'default'}}>{item.userEmail}</td>  
                            <td style={{cursor: 'default'}}><span>{item.lastGluco}</span>mg/dl</td>
                            <td style={{cursor: 'default'}}><span style={{ color: getGlucoState(item.lastGluco).color }}>{getGlucoState(item.lastGluco).text}</span></td>
                            <DateTimeCell value={item.accessDate} style={{cursor: 'default'}} />
                            <DateTimeCell value={item.lastGlucoDate} style={{cursor: 'default'}} />
                            <td style={{cursor: 'default'}}>
                              <div className="but-flex">
                                <div className="t_but mr-1" onClick={() => showGlucoData(item.userIdx)}>회원데이터</div>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <Pagination currentPage={currentPage} totalCount={totalCount} pageSize={pageSize} pageBlock={pageBlock} onPageChange={setCurrentPage} />
        </SectionContainer>
    );
}

export default GlucoDataPage; 